import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPendingRecordings, ignorePendingRecording } from '../api/client';
import { useAuth } from '../context/AuthContext';
import LoadingLeaf from '../components/LoadingLeaf';

export default function PendingRecordings() {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ignoring, setIgnoring] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user) loadRecordings();
  }, [user]);

  const loadRecordings = async () => {
    try {
      const res = await getPendingRecordings();
      setRecordings(res.data || []);
    } catch (err) {
      console.error('Failed to load recordings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleIgnore = async (source, externalId) => {
    setIgnoring(externalId);
    try {
      await ignorePendingRecording(source, externalId);
      setRecordings(prev => prev.filter(r => r.external_id !== externalId));
    } catch (err) {
      console.error('Failed to ignore recording:', err);
    } finally {
      setIgnoring(null);
    }
  };

  if (loading) return <LoadingLeaf fullScreen size={72} />;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <div style={styles.pageTitle}>Pending Recordings</div>
          <div style={styles.pageSubtitle}>Zoom recordings ready to import as sessions</div>
        </div>
      </div>

      {recordings.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>📹</div>
          <div style={styles.emptyTitle}>No pending recordings</div>
          <div style={styles.emptyText}>
            Zoom recordings will appear here automatically after your sessions end.<br />
            Make sure your Zoom account email matches your notification email in Settings.
          </div>
        </div>
      ) : (
        <>
          <div style={styles.countNote}>{recordings.length} recording{recordings.length !== 1 ? 's' : ''} ready to import</div>
          <div style={styles.grid}>
            {recordings.map(rec => (
              <div key={rec.id} style={styles.card}>
                <div style={styles.cardTop}>
                  <div style={styles.sourceTag}>
                    {rec.source === 'zoom' ? '🔵 Zoom' : '🔴 Google Meet'}
                  </div>
                  <div style={styles.recDate}>
                    {rec.recorded_at ? new Date(rec.recorded_at).toLocaleDateString('default', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                  </div>
                </div>

                <div style={styles.recTitle}>{rec.title}</div>

                {rec.participant_names?.length > 0 && (
                  <div style={styles.participantsSection}>
                    <div style={styles.participantsLabel}>Participants detected</div>
                    <div style={styles.tagRow}>
                      {rec.participant_names.slice(0, 6).map((name, i) => (
                        <span key={i} style={styles.tag}>{name}</span>
                      ))}
                      {rec.participant_names.length > 6 && (
                        <span style={styles.tag}>+{rec.participant_names.length - 6} more</span>
                      )}
                    </div>
                  </div>
                )}

                {rec.transcript_text && (
                  <div style={styles.transcriptNote}>✓ Transcript available</div>
                )}

                <div style={styles.actions}>
                  <button
                    style={styles.importBtn}
                    onClick={() => navigate(`/import-recording/${rec.source}/${rec.external_id}`)}
                  >
                    Review & Import
                  </button>
                  <button
                    style={styles.ignoreBtn}
                    onClick={() => handleIgnore(rec.source, rec.external_id)}
                    disabled={ignoring === rec.external_id}
                  >
                    {ignoring === rec.external_id ? 'Ignoring…' : 'Ignore'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  page: { padding: '36px 40px', fontFamily: "'DM Sans', sans-serif" },
  loading: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' },
  header: { marginBottom: '28px' },
  pageTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '28px', fontWeight: '400', color: 'var(--text-dark)', marginBottom: '4px' },
  pageSubtitle: { fontSize: '13px', color: 'var(--text-soft)' },
  countNote: { fontSize: '13.5px', color: 'var(--text-soft)', marginBottom: '20px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' },
  card: { background: 'white', border: '1px solid var(--border)', borderRadius: '14px', padding: '22px', boxShadow: 'var(--shadow-sm)', display: 'flex', flexDirection: 'column', gap: '14px' },
  cardTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  sourceTag: { fontSize: '12.5px', color: 'var(--text-soft)', fontWeight: '500' },
  recDate: { fontSize: '12px', color: 'var(--text-faint)' },
  recTitle: { fontSize: '15.5px', fontWeight: '500', color: 'var(--text-dark)', lineHeight: 1.4 },
  participantsSection: {},
  participantsLabel: { fontSize: '11.5px', fontWeight: '600', color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' },
  tagRow: { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  tag: { background: 'var(--mint)', color: 'var(--sage)', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '500' },
  transcriptNote: { fontSize: '12.5px', color: 'var(--sage)', fontWeight: '500' },
  actions: { display: 'flex', gap: '10px', marginTop: '4px' },
  importBtn: { flex: 1, padding: '10px 0', borderRadius: '8px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', fontWeight: '500', cursor: 'pointer' },
  ignoreBtn: { padding: '10px 16px', borderRadius: '8px', background: 'white', border: '1px solid var(--border)', color: 'var(--text-soft)', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', cursor: 'pointer' },
  emptyState: { textAlign: 'center', padding: '80px 40px' },
  emptyIcon: { fontSize: '40px', marginBottom: '16px' },
  emptyTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '22px', color: 'var(--text-dark)', marginBottom: '10px' },
  emptyText: { fontSize: '14px', color: 'var(--text-soft)', lineHeight: 1.7 },
};
